/**
 * In-flight email sign-in persistence.
 *
 * The "check your email" screen used to live purely in React state, which
 * meant it survived only as long as the document did. That's a bad bet on a
 * phone: the user has to leave the app to go read their code, and iOS
 * routinely tears down a backgrounded standalone PWA (our manifest sets
 * `display: standalone`) or discards a backgrounded Safari tab. They come
 * back, the app has relaunched at start_url, and they're staring at the
 * profile picker with a code in their hand and nowhere to type it — and
 * re-picking their profile just sends another email.
 *
 * Persisting the pending attempt means returning from the mail app lands
 * straight back on the code screen.
 *
 * Every accessor is try/catch-wrapped: storage can be unavailable outright
 * (iOS Lockdown Mode, blocked or full quota), and a sign-in screen is the
 * last place that should hard-fail on a storage exception.
 *
 * @see src/pages/LandingPage.tsx — writes on send/resend, resumes on mount
 * @see src/pages/AuthVerifyPage.tsx — clears when the magic-link path takes over
 * @see src/hooks/useAuth.ts — clearAllAuth drops this with the session
 */

/** localStorage key. Also removed by name in useAuth.clearAllAuth. */
export const PENDING_EMAIL_LOGIN_KEY = 'pending_email_login';

/** How long a persisted attempt stays resumable. Codes themselves live 10
 *  minutes server-side; the extra headroom means a late return still gets the
 *  code screen (with a working "Send a new code") rather than a silent bounce
 *  back to the picker. */
const PENDING_EMAIL_LOGIN_TTL_MS = 30 * 60 * 1000;

/** The subset of the picked profile the verification screen needs to
 *  re-render and to finalize sign-in. Structurally compatible with
 *  LandingPageUser — kept local so this module doesn't depend on a page. */
export interface PendingEmailLoginUser {
  id: string;
  name: string;
  role: 'office' | 'sales' | 'ops' | 'tech' | 'provisioning' | 'guest_tech';
  dualAccess?: boolean;
  emailLoginEnabled?: boolean;
}

export interface PendingEmailLogin {
  user: PendingEmailLoginUser;
  /** Which list the profile was picked from — drives dual-access routing. */
  loginContext: 'tech' | 'office' | null;
  maskedEmail: string;
  expiresAt: string;
  /** Client-clock stamp at write time. Freshness is judged client-clock
   *  against client-clock so a device with a skewed clock doesn't evict its
   *  own in-flight sign-in. (The same skew is why the verification screen no
   *  longer disables its inputs on a client-side expiry check.) */
  savedAt: number;
}

/**
 * The resumable attempt, or null when there isn't a usable one.
 *
 * `isSignedIn` is passed in rather than read here so this module stays out of
 * the identity keys that `lib/session` owns. Callers pass the result of
 * `getSession().displayName` — when someone is already signed in, the landing
 * page's auto-redirect owns that render and we must not flash the code screen
 * on the way through.
 */
export function readPendingEmailLogin(isSignedIn: boolean): PendingEmailLogin | null {
  if (isSignedIn) return null;
  try {
    const raw = localStorage.getItem(PENDING_EMAIL_LOGIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingEmailLogin;
    if (!parsed?.user?.id || !parsed.maskedEmail || !parsed.expiresAt) return null;
    if (typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > PENDING_EMAIL_LOGIN_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writePendingEmailLogin(
  pending: Omit<PendingEmailLogin, 'savedAt'>,
): void {
  try {
    localStorage.setItem(
      PENDING_EMAIL_LOGIN_KEY,
      JSON.stringify({ ...pending, savedAt: Date.now() } satisfies PendingEmailLogin),
    );
  } catch {
    // Storage blocked/full — the in-memory flow still works for as long as
    // the document lives, which is the pre-existing behaviour.
  }
}

export function clearPendingEmailLogin(): void {
  try {
    localStorage.removeItem(PENDING_EMAIL_LOGIN_KEY);
  } catch {
    /* best-effort */
  }
}
